import { Card, CardBody, CardHeader } from '@nextui-org/react'
import { useState } from 'react'
import { useAsync } from 'react-use'
import { InputOutputExamples } from '~/data/InputOutputExamples'
import { AgentRequest, AgentTaskStatus, CommandType, RuntimeSettings } from '~/protos/agents'
import { CurieSelect } from '../CurieSelect'
import { SimpleMultiSelect } from '../SimpleMultiSelect'
import { IconCurieStarButton } from '../icons'
import { TaskParams } from '../modals/task-forms/BaseTaskForm'
import { CampaignButton } from './CampaignButton'
import CampaignChat from './CampaignChat'
import { CampaignInputOutputToDWLForm } from './CampaignInputOutputExamples'
import MuleProjectUpload from './MuleProjectUpload'
import { CampaignTaskProgressBar } from './campaignBlocks/CampaignTaskProgressBar'

interface CampaignTryExampleProps {
  commandType?: CommandType
  currentGenerationSection: string
  exampleRef: React.RefObject<HTMLDivElement>
  handleChangeGenerationSection: (section: string) => void
  handleSetError: (error: boolean) => void
  handleSetRequest: (request: AgentRequest) => void
  handleSubmit: (request: AgentRequest, isExample?: boolean) => void
  handleTryOwn: () => void
  isDisabled: boolean
  isError: boolean
  messages: Message[]
  preSubmitCheck: (request: AgentRequest, section: string) => boolean
  progress: number
  request: AgentRequest
  resetTaskProgress: () => void
  taskStatus: AgentTaskStatus
}

const DataWeaveGenerator = ({
  currentGenerationSection,
  exampleRef,
  handleChangeGenerationSection,
  handleSetError,
  handleSetRequest,
  handleSubmit,
  handleTryOwn,
  isDisabled,
  isError,
  messages,
  preSubmitCheck,
  progress,
  request,
  resetTaskProgress,
  taskStatus
}: CampaignTryExampleProps) => {
  const [example, setExample] = useState<string[]>([InputOutputExamples[0].label])
  const [inputDefaultValue, setInputDefaultValue] = useState<string | undefined>(
    InputOutputExamples[0].input
  )
  const [outputDefaultValue, setOutputDefaultValue] = useState<string | undefined>(
    InputOutputExamples[0].output
  )
  const [inputTypeDefaultValue, setInputTypeDefaultValue] = useState<string | undefined>(
    InputOutputExamples[0].inputType
  )
  const isTaskCompleted = taskStatus === AgentTaskStatus.DOCUMENT_GENERATED

  return (
    <div ref={exampleRef} className='h-fit w-screen flex items-center justify-center pt-5 pb-5'>
      <Card className='max-w-screen w-10/12 p-6 bg-zinc-50' shadow='sm'>
        <CardHeader className='flex gap-8 caret-transparent'>
          <div>
            <p className='text-5xl tracking-tighter'>
              Try one of our{' '}
              <span className='bg-gradient-to-r from-[#8063FA] to-[#C8A7FF] text-transparent bg-clip-text'>
                examples first
              </span>
              , then see
            </p>
            <p className='text-5xl tracking-tighter'>how Curie works with your own data</p>
          </div>
        </CardHeader>
        <CardBody>
          <div className='flex flex-col gap-3'>
            <div className='display flex items-center text-default-500'>
              <span className='mr-2 label font-medium text-md text-[#717680] mb-[10px] mt-[5px]'>
                Examples:
              </span>
              <CurieSelect
                placeholder='Select an example'
                className='w-64 pb-0'
                selectedKeys={example}
                items={InputOutputExamples.map(example => ({
                  label: example.label,
                  key: example.label
                }))}
                onSelectionChange={selectedKeys => {
                  if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
                    return
                  }
                  const key = selectedKeys.values().next().value as string
                  setExample([key])
                  const example = InputOutputExamples.find(example => example.label === key)!
                  setInputDefaultValue(example.input)
                  setOutputDefaultValue(example.output)
                  setInputTypeDefaultValue(example.inputType)
                }}
              />
            </div>
            <CampaignInputOutputToDWLForm
              key={
                String(inputDefaultValue) +
                String(outputDefaultValue) +
                String(inputTypeDefaultValue)
              }
              minRows={15}
              maxRows={15}
              onChange={(task: TaskParams) => handleSetRequest(task.request)}
              inputDefaultValue={inputDefaultValue}
              outputDefaultValue={outputDefaultValue}
              inputTypeDefaultValue={inputTypeDefaultValue}
              inputClassName='h-[320px]'
              collapsible
              onError={() => handleSetError(true)}
              onErrorClear={() => handleSetError(false)}
              isReadOnly={true}
            />

            <div className='w-full flex justify-center mt-4'>
              <CampaignButton
                id='create-account'
                type='submit'
                className='w-36 h-12 disabled:opacity-60'
                isDisabled={isDisabled || isError}
                onClick={() => {
                  if (!preSubmitCheck(request, 'example')) return
                  resetTaskProgress()
                  if (exampleRef?.current)
                    exampleRef.current.scrollIntoView({ behavior: 'instant', block: 'start' })
                  handleChangeGenerationSection('example')
                  handleSubmit(request, true)
                }}
              >
                <div className='flex flex-row gap-2'>
                  <IconCurieStarButton className='w-5 h-5' />
                  Generate
                </div>
              </CampaignButton>
            </div>
            <div className='w-full py-4 flex'>
              {currentGenerationSection === 'example' &&
              taskStatus !== AgentTaskStatus.NONE_TASK_STATUS ? (
                <CampaignTaskProgressBar taskStatus={taskStatus} percentage={progress} />
              ) : null}
            </div>
            <div>
              {currentGenerationSection === 'example' &&
              Array.isArray(messages) &&
              messages.length ? (
                <CampaignChat messages={messages} />
              ) : null}
              {currentGenerationSection === 'example' && isTaskCompleted && (
                <CampaignButton onClick={handleTryOwn} className='w-64 h-12 ml-10 -mt-6'>
                  Try with your own data
                </CampaignButton>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

const DiagramGenerator = ({
  currentGenerationSection,
  exampleRef,
  handleChangeGenerationSection,
  handleSetError,
  handleSetRequest,
  handleSubmit,
  handleTryOwn,
  isDisabled,
  isError,
  messages,
  preSubmitCheck,
  progress,
  request,
  resetTaskProgress,
  taskStatus
}: CampaignTryExampleProps) => {
  const [uploadPath, setUploadPath] = useState<string | undefined>(undefined)
  const exampleFlowNames = [
    'get:\\members\\(memberId):loyalty-management-api-config',
    'post:\\members:application\\json:loyalty-management-api-config',
    'process-orders-flow'
  ]
  const exampleFlowNamesString = exampleFlowNames.join(', ')
  const isTaskCompleted = taskStatus === AgentTaskStatus.DOCUMENT_GENERATED
  const items = exampleFlowNames.map(flowName => ({
    value: flowName,
    label: flowName
  }))

  useAsync(async () => {
    const request = AgentRequest.fromPartial({
      command: { commandString: exampleFlowNamesString },
      metadata: {
        codeContext: {
          connType: 'S3',
          s3TarFilePath: uploadPath
        }
      },
      runtimeSettings: RuntimeSettings.fromPartial({
        javaVersion: '8.0',
        mavenVersion: '3.8'
      })
    })
    handleSetRequest(request)
  }, [currentGenerationSection, uploadPath])

  return (
    <div ref={exampleRef} className='h-fit w-screen flex items-center justify-center pt-5 pb-5'>
      <Card className='max-w-screen w-10/12 p-6 bg-zinc-50' shadow='sm'>
        <CardHeader className='flex gap-8 caret-transparent'>
          <div>
            <p className='text-5xl tracking-tighter'>
              Try one of our{' '}
              <span className='bg-gradient-to-r from-[#8063FA] to-[#C8A7FF] text-transparent bg-clip-text'>
                examples first
              </span>
              , then see
            </p>
            <p className='text-5xl tracking-tighter'>how Curie works with your own data</p>
          </div>
        </CardHeader>
        <CardBody>
          <div className='flex flex-col gap-3'>
            <div className='display flex items-center text-default-500'>
              <span className='mr-2 label font-medium text-md text-[#717680] mb-[10px] mt-[5px]'>
                Examples:
              </span>
            </div>

            <MuleProjectUpload onComplete={setUploadPath} withExamplePrefilled={true} />

            <SimpleMultiSelect
              label={'Flow Names'}
              placeholder={exampleFlowNamesString}
              selectedKeys={exampleFlowNames}
              onSelectionChange={() => {}}
              readOnly={true}
              disabled={false}
              items={items}
              isMultiSelect={true}
              size='sm'
            />

            <div className='w-full flex justify-center mt-4'>
              <CampaignButton
                id='create-account'
                type='submit'
                className='w-36 h-12 disabled:opacity-60'
                isDisabled={isDisabled || isError}
                onClick={() => {
                  if (!preSubmitCheck(request, 'example')) return
                  resetTaskProgress()
                  if (exampleRef?.current)
                    exampleRef.current.scrollIntoView({ behavior: 'instant', block: 'start' })
                  handleChangeGenerationSection('example')
                  handleSubmit(request, true)
                }}
              >
                <div className='flex flex-row gap-2'>
                  <IconCurieStarButton className='w-5 h-5' />
                  Generate
                </div>
              </CampaignButton>
            </div>
            <div className='w-full py-4 flex'>
              {currentGenerationSection === 'example' &&
              taskStatus !== AgentTaskStatus.NONE_TASK_STATUS ? (
                <CampaignTaskProgressBar taskStatus={taskStatus} percentage={progress} />
              ) : null}
            </div>
            <div>
              {currentGenerationSection === 'example' &&
              Array.isArray(messages) &&
              messages.length ? (
                <CampaignChat messages={messages} />
              ) : null}
              {currentGenerationSection === 'example' && isTaskCompleted && (
                <CampaignButton onClick={handleTryOwn} className='w-64 h-12 ml-10 -mt-6'>
                  Try with your own data
                </CampaignButton>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

const CampaignTryExample = ({
  commandType,
  currentGenerationSection,
  exampleRef,
  handleChangeGenerationSection,
  handleSetError,
  handleSetRequest,
  handleSubmit,
  handleTryOwn,
  isDisabled,
  isError,
  messages,
  preSubmitCheck,
  progress,
  request,
  resetTaskProgress,
  taskStatus
}: CampaignTryExampleProps) => {
  switch (commandType) {
    case CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION: // DataWeave generator
      return (
        <DataWeaveGenerator
          currentGenerationSection={currentGenerationSection}
          exampleRef={exampleRef}
          handleChangeGenerationSection={handleChangeGenerationSection}
          handleSetError={handleSetError}
          handleSetRequest={handleSetRequest}
          handleSubmit={handleSubmit}
          handleTryOwn={handleTryOwn}
          isDisabled={isDisabled}
          isError={isError}
          messages={messages}
          preSubmitCheck={preSubmitCheck}
          progress={progress}
          request={request}
          resetTaskProgress={resetTaskProgress}
          taskStatus={taskStatus}
        />
      )
      break
    case CommandType.GENERATE_IMAGE: // Diagram generator
      return (
        <DiagramGenerator
          currentGenerationSection={currentGenerationSection}
          exampleRef={exampleRef}
          handleChangeGenerationSection={handleChangeGenerationSection}
          handleSetError={handleSetError}
          handleSetRequest={handleSetRequest}
          handleSubmit={handleSubmit}
          handleTryOwn={handleTryOwn}
          isDisabled={isDisabled}
          isError={isError}
          messages={messages}
          preSubmitCheck={preSubmitCheck}
          progress={progress}
          request={request}
          resetTaskProgress={resetTaskProgress}
          taskStatus={taskStatus}
        />
      )
      break
    default:
      return null
  }
}

export default CampaignTryExample
